import { useState } from 'react';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import type { NoteOnBehalfContext } from '@/api/types';
import {
  Button,
  Caption,
  ErrorState,
  Eyebrow,
  Field,
  Loading,
  Modal,
  Notice,
  Segmented,
  TextArea,
  TextInput,
} from '@/components/ui';
import { formatDate, formatRange } from '@/lib/format';

type TaskStatus = 'complete' | 'declined' | 'not_applicable' | 'unable';

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'complete', label: 'Complete' },
  { value: 'declined', label: 'Declined' },
  { value: 'not_applicable', label: 'N/A' },
  { value: 'unable', label: 'Unable' },
];

/** The three statuses that are exceptions to planned care, and so need a reason. */
const REASON_REQUIRED: TaskStatus[] = ['declined', 'not_applicable', 'unable'];

/**
 * Record a visit note for a caregiver who cannot write it themselves.
 *
 * The alternative in use today is signing in as the caregiver, which falsifies
 * authorship and defeats every audit control in the product. So the note is
 * stored as the coordinator's, flagged, with a reason — and the form says so
 * plainly, because a coordinator who thinks they are writing *as* the caregiver
 * will word the note as if they had been there.
 *
 * Nothing is pre-selected, for the same reason as the caregiver's own screen
 * (D-09): a default would record care nobody confirmed happened.
 */
export function RecordNoteOnBehalf({
  visitId,
  onClose,
  onDone,
}: {
  visitId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const context = useApi<NoteOnBehalfContext>(`/admin/visits/${visitId}/note/on-behalf`);
  const [statuses, setStatuses] = useState<Record<string, TaskStatus>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  // What is still blank is not yet a mistake. This form has a required field
  // per planned task, and marking them all in red before the coordinator has
  // typed anything reads as a screen full of errors rather than a form.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (key: string) => setTouched((current) => ({ ...current, [key]: true }));

  const data = context.data;
  const tasks = data?.planned_tasks ?? [];

  // Mirrors the server's own submission rules, so the button is disabled for
  // the same reasons the save would fail. The server still checks; this only
  // keeps a coordinator from filling the form and being told at the end.
  const everyTaskAnswered = tasks.every((task) => {
    const status = statuses[task.task_id];
    if (!status) return false;
    return !REASON_REQUIRED.includes(status) || Boolean(reasons[task.task_id]?.trim());
  });
  const ready = everyTaskAnswered && summary.trim().length > 0 && why.trim().length >= 3;

  async function submit() {
    setBusy(true);
    setProblems([]);
    try {
      await api.post(`/admin/visits/${visitId}/note/on-behalf`, {
        summary: summary.trim(),
        reason: why.trim(),
        input_method: 'typed',
        task_statuses: tasks.map((task) => ({
          task_id: task.task_id,
          name: task.name ?? '',
          status: statuses[task.task_id],
          reason: reasons[task.task_id]?.trim() || null,
        })),
      });
      onDone();
    } catch (caught) {
      const error = caught as ApiError;
      setProblems(error.problems.length > 0 ? error.problems : [error.message]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Record a note for the caregiver" onClose={onClose} wide>
      {context.loading ? <Loading /> : null}
      {context.error ? (
        <ErrorState message={context.error.message} onRetry={context.reload} />
      ) : null}

      {data ? (
        <div className="stack">
          <Notice tone="info" title="This note will be filed under your name">
            {data.caregiver_name ?? 'The caregiver'} stays recorded as the person who delivered the
            care. The note is marked as recorded on their behalf, with your reason, and the family
            update follows the usual approval path.
          </Notice>

          <Caption>
            {data.recipient_name} · {formatDate(data.scheduled_start)} ·{' '}
            {formatRange(data.scheduled_start, data.scheduled_end)}
          </Caption>

          {data.final_note_exists ? (
            <Notice tone="attention" title="This visit already has a final note">
              Open the note and submit a correction instead — a visit has one final note.
            </Notice>
          ) : null}

          {problems.length > 0 ? (
            <Notice tone="urgent" title="This note is not ready to submit">
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {problems.map((problem, index) => (
                  <li key={index}>{problem}</li>
                ))}
              </ul>
            </Notice>
          ) : null}

          <Field
            label="Why is the caregiver not writing this themselves?"
            hint="Stored with the note. Recorded so the record says what happened."
            error={!touched.why || why.trim().length >= 3 ? null : 'A reason is required'}
          >
            {(props) => (
              <TextInput
                {...props}
                value={why}
                onChange={(event) => setWhy(event.target.value)}
                onBlur={() => touch('why')}
                placeholder="Read over the phone — caregiver's phone had no battery"
                autoFocus
              />
            )}
          </Field>

          <div className="divider" />

          <Eyebrow>Every planned task needs a status</Eyebrow>
          {tasks.length === 0 ? (
            <Caption>This plan has no tasks, so only the summary is needed.</Caption>
          ) : null}

          {tasks.map((task) => {
            const status = statuses[task.task_id];
            const needsReason = Boolean(status && REASON_REQUIRED.includes(status));
            return (
              <div key={task.task_id} className="stack stack--tight">
                <div>
                  <p className="strong">{task.name ?? task.task_id}</p>
                  {task.instructions ? <Caption>{task.instructions}</Caption> : null}
                </div>
                <Segmented
                  label={`Status for ${task.name ?? task.task_id}`}
                  options={STATUS_OPTIONS}
                  value={status ?? ('' as TaskStatus)}
                  onChange={(next) =>
                    setStatuses((current) => ({ ...current, [task.task_id]: next }))
                  }
                />
                {needsReason ? (
                  <Field
                    label="Reason"
                    error={
                      !touched[task.task_id] || reasons[task.task_id]?.trim()
                        ? null
                        : 'This status needs a reason'
                    }
                  >
                    {(props) => (
                      <TextInput
                        {...props}
                        value={reasons[task.task_id] ?? ''}
                        onChange={(event) =>
                          setReasons((current) => ({
                            ...current,
                            [task.task_id]: event.target.value,
                          }))
                        }
                        onBlur={() => touch(task.task_id)}
                        placeholder="What the caregiver reported"
                      />
                    )}
                  </Field>
                ) : null}
              </div>
            );
          })}

          <div className="divider" />

          <Field
            label="Visit summary"
            hint="In the caregiver's words, as they reported them. The family sees this after approval."
            error={!touched.summary || summary.trim() ? null : 'A summary is required'}
          >
            {(props) => (
              <TextArea
                {...props}
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                onBlur={() => touch('summary')}
                rows={5}
              />
            )}
          </Field>

          <div className="row row--between">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              busy={busy}
              disabled={!ready || data.final_note_exists}
            >
              Submit note
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
